import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { ROOT_BACKEND_API } from "../constants";

export const getFormattedDate = () => {
  const date = new Date();

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

export const formatNumberWithCommas = (number) => {
  if (number === "") return "";
  number = Number(number);
  const formatter = new Intl.NumberFormat("en-US");
  return formatter.format(number);
};

export const uploadDirectorPan = async (file) => {
  const formData = new FormData();

  formData.append("file", file);
  formData.append("document_type", "identity_proof");
  formData.append("document_sub_type", "pancard");
  formData.append("document_type_code", null);
  formData.append("document_number", null);
  formData.append("document_number_identifier", "pancard");
  formData.append("level", "user");

  return axios
    .post(ROOT_BACKEND_API + "/document/v2/upload", formData, {
      headers: {
        token: localStorage.getItem("access_token"),
        "Content-Type": "multipart/form-data",
      },
    })
    .then((response) => {
      if (response.status >= 200 && response.status < 300) {
        return response.data;
      } else {
        throw new Error("Upload failed with status: " + response.status);
      }
    })
    .catch((error) => {
      if (axios.isCancel(error)) {
        throw new Error("Upload canceled");
      } else if (error.response && error.response.status === 422) {
        throw error;
      } else {
        console.error("An unexpected error occurred", error);
        throw error;
      }
    });
};

export const uploadFileWithProgress = (
  file,
  setProgress,
  setFailed,
  setSuccess,
  documentData,
  signal // Add the signal parameter
) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("document_type", documentData.documentType);
  formData.append("document_sub_type", documentData.documentSubType);
  formData.append("document_type_code", documentData?.document_type_code);
  formData.append("document_number", documentData.documentNumber);
  formData.append(
    "document_number_identifier",
    documentData.documentNumberIdentifier
  );
  formData.append("level", documentData.level);

  let backend_api_route = "/document/upload";

  if (documentData.documentSubType === "aadhaar_card") {
    backend_api_route = "/document/v2/upload";
  }
  return axios
    .post(ROOT_BACKEND_API + backend_api_route, formData, {
      headers: {
        token: localStorage.getItem("access_token"),
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        const progress = Math.round(
          (progressEvent.loaded / progressEvent.total) * 100
        );
        setProgress(progress);
      },
      signal, // Pass the signal to Axios
    })
    .then((response) => {
      if (response.status >= 200 && response.status < 300) {
        setSuccess(true);
        setFailed(false); // Upload successful, no retry needed
        return response.data;
      } else {
        setFailed(true); // Trigger retry if unexpected response
        setSuccess(false);
        throw new Error("Upload failed with status: " + response.status);
      }
    })
    .catch((error) => {
      if (axios.isCancel(error)) {
        throw new Error("Upload canceled");
      } else if (error.response && error.response.status === 422) {
        // Backend responded with an error (422)
        setFailed(true); // This will trigger the retry button
        setSuccess(false);
        throw error;
      } else {
        console.error("An unexpected error occurred", error);
        setFailed(true); // Retry on any network or other error
        setSuccess(false);
        throw error;
      }
    });
};

export const getLatestUploadedDocument = (documents, documentSubType) => {
  if (!Array.isArray(documents) || documents.length === 0) {
    return null;
  }

  // Filter documents based on document_sub_type and then find the latest uploaded one
  return documents
    .filter((doc) => doc.document_sub_type === documentSubType)
    .reduce((latest, current) => {
      const latestUploadedAt = new Date(latest.uploaded_at);
      const currentUploadedAt = new Date(current.uploaded_at);

      return currentUploadedAt > latestUploadedAt ? current : latest;
    }, documents[0] || null);
};

// here steps is array of of objects which has individual step
export const findCurrentTimelineStepOrSubstep = (steps) => {
  const length = steps.length;
  for (let i = 0; i < length; i++) {
    const { substeps, step_id } = steps[i];

    const incompleteSubstep = substeps.find(({ completed }) => !completed);
    if (incompleteSubstep) {
      incompleteSubstep.step_id = step_id;
      return incompleteSubstep;
    }
  }
  return -1;
};

export const getFirstDigitAfterDecimal = (num) => {
  const decimalPart = num.toString().split(".")[1];
  return decimalPart ? parseInt(decimalPart[0], 10) : 0;
};

export const storeBrowserUUID = () => {
  const existingUUID = localStorage.getItem("browser_uuid");
  if (!existingUUID) {
    const newUUID = uuidv4();
    localStorage.setItem("browser_uuid", newUUID);
  }
};

export const extractJtiFromToken = (token) => {
  if (!token) return null;

  // Split the token into its three parts: header, payload, signature
  const payloadPart = token.split(".")[1];

  // Decode the payload from Base64
  const decodedPayload = atob(payloadPart);

  // Parse the decoded payload to JSON
  const payloadObject = JSON.parse(decodedPayload);

  // Extract and return the jti (JWT ID)
  return payloadObject.jti;
};

export function isValidEmail(email) {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}

export function isValidPanCard(pan) {
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  return panRegex.test(pan);
}

export function isValidWebsite(url) {
  const websiteRegex =
    /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+(\/[^\s]*)?$/;
  return websiteRegex.test(url);
}
